/** biome-ignore-all lint/a11y/noStaticElementInteractions: tooltip trigger + portaled hover bridge */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: clickToOpen uses pointer; focus handled separately */
import { smartenChildren } from "@utils/text";
import {
	type Accessor,
	createContext,
	createEffect,
	createMemo,
	createSignal,
	createUniqueId,
	type JSX,
	onCleanup,
	type ParentComponent,
	children as resolveChildren,
	Show,
	splitProps,
	useContext,
} from "solid-js";
import { Portal } from "solid-js/web";
import "./Tooltip.css";

export type TooltipPlacement =
	| "bottom"
	| "bottom left"
	| "bottom right"
	| "bottom start"
	| "bottom end"
	| "top"
	| "top left"
	| "top right"
	| "top start"
	| "top end"
	| "left"
	| "left top"
	| "left bottom"
	| "start"
	| "start top"
	| "start bottom"
	| "right"
	| "right top"
	| "right bottom"
	| "end"
	| "end top"
	| "end bottom";

type TooltipProps = {
	children?: JSX.Element;
	/** Defaults to the id assigned by {@link TooltipTrigger}. */
	id?: string;
	placement?: TooltipPlacement;
	borderRadius?: "standard" | "large";
	theme?: "light" | "dark";
	width?: "standard" | "wide";
	clickToOpen?: boolean;
	class?: string;
	renderContent?: () => JSX.Element;
	/**
	 * Applies typographic smart quotes and em dashes to string children.
	 * Element children are preserved.
	 * @default false
	 */
	smarten?: boolean;
};

const TooltipShellContext = createContext<{ tooltipId: string }>();

const FOCUSABLE_DESCIBEDBY_SELECTOR =
	'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Elements that receive `aria-describedby` while the tooltip is open. */
const getDescribableElements = (root: HTMLElement): HTMLElement[] => {
	const focusable = Array.from(
		root.querySelectorAll<HTMLElement>(FOCUSABLE_DESCIBEDBY_SELECTOR),
	).filter((el) => !el.closest("[aria-hidden='true']"));
	if (focusable.length > 0) {
		return focusable;
	}
	return [root];
};

const mergeDescribedBy = (
	el: HTMLElement,
	tooltipId: string,
	originals: Map<HTMLElement, string | null>,
) => {
	if (!originals.has(el)) {
		originals.set(el, el.getAttribute("aria-describedby"));
	}
	const current = el.getAttribute("aria-describedby");
	const ids = current ? current.split(/\s+/).filter(Boolean) : [];
	if (!ids.includes(tooltipId)) {
		ids.push(tooltipId);
	}
	el.setAttribute("aria-describedby", ids.join(" "));
};

const restoreDescribedBy = (originals: Map<HTMLElement, string | null>) => {
	for (const [el, value] of originals) {
		if (value === null) {
			el.removeAttribute("aria-describedby");
		} else {
			el.setAttribute("aria-describedby", value);
		}
	}
	originals.clear();
};

/**
 * Tooltip content component for contextual information.
 *
 * [Design System →](http://localhost:5174/component/Tooltip)
 */
export const Tooltip: ParentComponent<TooltipProps> = (props) => {
	const shell = useContext(TooltipShellContext);
	const placement = () => props.placement || "top";
	const resolved = resolveChildren(() => props.children);
	const resolvedId = () => props.id ?? shell?.tooltipId;
	const themeClass = () =>
		props.theme ? `gvtui-tooltip--${props.theme}` : "gvtui-tooltip--light";
	return (
		<div
			id={resolvedId()}
			class={`gvtui-tooltip ${themeClass()} ${props.borderRadius ? `gvtui-tooltip--border-radius-${props.borderRadius}` : ""} ${props.width ? `gvtui-tooltip--${props.width}` : ""} ${props.class ? props.class : ""}`}
			role="tooltip"
			data-placement={placement()}
		>
			<div class="gvtui-tooltip-content">
				{props.smarten === true
					? (smartenChildren(resolved()) as JSX.Element)
					: resolved()}
			</div>
		</div>
	);
};

type TooltipTriggerProps = {
	children: JSX.Element;
	/** Added to the trigger wrapper, e.g. to make it fill its row rather than shrink to its text. */
	class?: string;
	placement?: TooltipPlacement;
	clickToOpen?: boolean;
	/** Milliseconds before open on hover/focus (default `0`). Ignored when `clickToOpen` is set. */
	delay?: number;
	/** When set, only the first child is rendered (no tooltip or trigger wrapper). */
	disabled?: boolean;
	/**
	 * Hover tooltips hide when their trigger is pressed and stay hidden until the pointer
	 * leaves. Set this for a tooltip whose text reports the result of the click (e.g.
	 * "Add to board" → "Update"), so it stays on screen to show it.
	 */
	keepOpenOnClick?: boolean;
	/** Render function for tooltip content. When provided, content is deferred until the tooltip first opens. Takes precedence over the second child. */
	renderContent?: () => JSX.Element;
	/** When false, keeps the requested placement instead of flipping to the opposite side. */
	flip?: boolean;
	/**
	 * Opens only while something in the trigger is cut off at its side — an ellipsis label,
	 * say — so a tooltip repeating that text appears just when there is more to read. Checked
	 * on each open, so it follows the trigger being resized.
	 */
	onlyWhenTruncated?: boolean;
	/**
	 * Selector for an ancestor whose edge the tooltip sits against on its placement side,
	 * instead of the trigger's own edge — e.g. `".side-bar"` so tooltips on a narrow menu
	 * point at the menu's edge. The trigger still sets the cross-axis position.
	 */
	anchorTo?: string;
	/**
	 * When set, applied to the outer trigger wrapper so non-interactive children
	 * (e.g. truncated static text) can receive focus for the tooltip.
	 */
	tabIndex?: number;
};

/**
 * True when `root` or anything inside it clips its content horizontally. Height is left out:
 * a single clipped line can report a `scrollHeight` a pixel taller than its box from glyph
 * overhang alone, which would open the tooltip over text that is shown in full.
 */
const hasTruncatedContent = (root: Element): boolean =>
	[root, ...root.querySelectorAll("*")].some(
		(el) =>
			el.scrollWidth > el.clientWidth &&
			getComputedStyle(el).overflowX !== "visible",
	);

function getTooltipChildren(children: JSX.Element): JSX.Element[] {
	const resolved =
		typeof children === "function"
			? (children as () => JSX.Element | JSX.Element[])()
			: children;
	if (Array.isArray(resolved)) {
		return resolved;
	}
	return [resolved];
}

function TooltipTriggerPassthrough(props: Pick<TooltipTriggerProps, "children">) {
	const children = getTooltipChildren(props.children);
	return children[0];
}

/**
 * Shared across every hover trigger so that neighbouring tooltips behave as one contiguous
 * set, the way native tooltips do: only the first open waits out its delay, and moving on to
 * another trigger swaps the tooltip straight away.
 */
const WARM_GRACE_MS = 500;
let openTooltipCount = 0;
let warmUntil = 0;
let closeOpenTooltip: (() => void) | undefined;

/** True while a tooltip is on screen, and for a short grace period after the last one closes. */
const tooltipsAreWarm = () => openTooltipCount > 0 || Date.now() < warmUntil;

/**
 * Tooltips are for mouse and keyboard only. A tap also focuses the control it lands on, and
 * that focus must not open one; a key press afterwards hands focus back to the keyboard.
 */
let lastPressWasTouch = false;
if (typeof document !== "undefined") {
	document.addEventListener(
		"pointerdown",
		(e) => {
			lastPressWasTouch = e.pointerType === "touch";
		},
		true,
	);
	document.addEventListener(
		"keydown",
		() => {
			lastPressWasTouch = false;
		},
		true,
	);
}

/** Records a newly opened tooltip, closing any other still showing. Returns its unregister. */
const registerOpenTooltip = (closeNow: () => void) => {
	const previous = closeOpenTooltip;
	openTooltipCount += 1;
	closeOpenTooltip = closeNow;
	previous?.();
	return () => {
		if (closeOpenTooltip === closeNow) {
			closeOpenTooltip = undefined;
		}
		openTooltipCount = Math.max(0, openTooltipCount - 1);
		if (openTooltipCount === 0) {
			warmUntil = Date.now() + WARM_GRACE_MS;
		}
	};
};

/**
 * Wrapper that shows tooltip on hover, focus, or click (`clickToOpen`).
 * While open, sets `aria-describedby` on the trigger control(s) to the portaled
 * tooltip id. Escape dismisses the tip (focus/hover and click modes); click mode
 * also toggles `aria-expanded`.
 *
 * [Design System →](http://localhost:5174/component/Tooltip)
 */
type AnchorRect = Pick<
	DOMRect,
	"top" | "right" | "bottom" | "left" | "width" | "height"
>;

/** The trigger's box, with its main-axis edges taken from the `anchorTo` ancestor when there is one. */
const anchorRectFor = (
	trigger: HTMLElement,
	anchorTo: string | undefined,
	placement: TooltipPlacement,
): AnchorRect => {
	const rect = trigger.getBoundingClientRect();
	const edges = anchorTo
		? trigger.closest(anchorTo)?.getBoundingClientRect()
		: undefined;
	if (!edges) {
		return rect;
	}
	const { top, right, bottom, left, width, height } = rect;
	const primary = placement.split(" ")[0];
	if (primary === "top" || primary === "bottom") {
		return {
			left,
			right,
			width,
			top: edges.top,
			bottom: edges.bottom,
			height: edges.height,
		};
	}
	return {
		top,
		bottom,
		height,
		left: edges.left,
		right: edges.right,
		width: edges.width,
	};
};

const setTooltipArrowOffset = (
	tooltipElement: HTMLElement,
	triggerRect: AnchorRect,
	left: number,
	tooltipWidth: number,
	resolvedPrimary: string,
	resolvedAlignment: string,
) => {
	const horizontalPlacement =
		resolvedPrimary === "top" || resolvedPrimary === "bottom";
	const centeredAlign =
		resolvedAlignment !== "left" &&
		resolvedAlignment !== "right" &&
		resolvedAlignment !== "top" &&
		resolvedAlignment !== "bottom";
	if (!horizontalPlacement || !centeredAlign) {
		tooltipElement.style.removeProperty("--gvtui-tooltip-arrow-x");
		return;
	}
	const triggerCenterX = triggerRect.left + triggerRect.width / 2;
	const arrowX = Math.max(
		16,
		Math.min(tooltipWidth - 16, triggerCenterX - left),
	);
	tooltipElement.style.setProperty("--gvtui-tooltip-arrow-x", `${arrowX}px`);
};

const TooltipTriggerInner = (props: Omit<TooltipTriggerProps, "disabled" | "renderContent"> & { renderContent?: () => JSX.Element }) => {
	const tooltipId = `gvtui-tooltip-${createUniqueId()}`;
	const [isVisible, setIsVisible] = createSignal(false);
	/** Suppresses the open animation when replacing a tooltip that is already on screen. */
	const [openedWarm, setOpenedWarm] = createSignal(false);
	const [triggerDisabled, setTriggerDisabled] = createSignal(false);
	const placement = () => props.placement || "top";
	let triggerRef: HTMLDivElement | undefined;
	let tooltipRef: HTMLSpanElement | undefined;
	let hideTimeoutId: number | undefined;
	let showDelayTimeoutId: number | undefined;
	/** Set by pressing the trigger; stops hover/focus reopening the tooltip until the pointer or focus moves on. */
	let dismissedByPress = false;
	let pointerInside = false;

	/**
	 * A menu or popover opened from the trigger covers the tooltip's job, so the tooltip never
	 * shows while one is open. The pointer or focus leaving for that popup doesn't count as
	 * moving on either: closing it hands focus back to the trigger, which would otherwise
	 * reopen the tooltip. A click-opened tooltip sets `aria-expanded` on the trigger itself,
	 * so it is left out.
	 */
	const triggerHasOpenPopup = () =>
		!props.clickToOpen &&
		triggerRef?.querySelector("[aria-expanded='true']") != null;

	const clearHideTimeout = () => {
		if (hideTimeoutId !== undefined) {
			window.clearTimeout(hideTimeoutId);
			hideTimeoutId = undefined;
		}
	};

	const clearShowDelay = () => {
		if (showDelayTimeoutId !== undefined) {
			window.clearTimeout(showDelayTimeoutId);
			showDelayTimeoutId = undefined;
		}
	};

	/** Open delay in ms; invalid values → 0 (avoids `setTimeout(fn, NaN)` instant fire). */
	const delayMs = (): number => {
		const d = props.delay;
		if (d == null) {
			return 0;
		}
		const n = Number(d);
		return Number.isFinite(n) ? Math.max(0, n) : 0;
	};

	/** Show now; clears pending hide and open timers. */
	const showTooltipNow = () => {
		clearHideTimeout();
		clearShowDelay();
		if (!triggerHasOpenPopup()) setIsVisible(true);
	};

	/**
	 * Schedule open after `delay` (hover + keyboard); `delay` 0 uses immediate path, as does
	 * arriving from another tooltip while the set is still warm.
	 */
	const scheduleOpen = () => {
		if (dismissedByPress) {
			return;
		}
		if (
			props.onlyWhenTruncated &&
			!(triggerRef && hasTruncatedContent(triggerRef))
		) {
			return;
		}
		const ms = delayMs();
		const warm = tooltipsAreWarm();
		if (ms === 0 || warm) {
			setOpenedWarm(warm);
			showTooltipNow();
			return;
		}
		clearHideTimeout();
		clearShowDelay();
		showDelayTimeoutId = window.setTimeout(() => {
			showDelayTimeoutId = undefined;
			setOpenedWarm(false);
			if (!triggerHasOpenPopup()) setIsVisible(true);
		}, ms);
	};

	const hideTooltip = () => {
		clearShowDelay();
		clearHideTimeout();
		hideTimeoutId = window.setTimeout(() => {
			hideTimeoutId = undefined;
			setIsVisible(false);
		}, 100);
	};

	/** Skips the hide grace period, for when another tooltip is taking this one's place. */
	const hideTooltipNow = () => {
		clearShowDelay();
		clearHideTimeout();
		setIsVisible(false);
	};

	// Click-opened tooltips stay out of the shared set: they behave like popovers, so they
	// should neither be dismissed by hovering elsewhere nor make other tooltips instant.
	createEffect(() => {
		if (!isVisible() || props.clickToOpen) {
			return;
		}
		onCleanup(registerOpenTooltip(hideTooltipNow));
	});

	/** Hover path: skip show when entering while a mouse button is held (e.g. drag into trigger). */
	const handleTriggerPointerEnter = (e: PointerEvent) => {
		if (props.clickToOpen || e.pointerType === "touch") {
			return;
		}
		if (e.buttons !== 0) {
			return;
		}
		// Arriving back after leaving while a popup was open; see `triggerHasOpenPopup`.
		if (!pointerInside && !triggerHasOpenPopup()) dismissedByPress = false;
		pointerInside = true;
		scheduleOpen();
	};

	/** Portaled tooltip hover bridge: always refresh visibility (no buttons check — avoids closing while moving onto the tip). */
	const handleTooltipBridgePointerEnter = (e: PointerEvent) => {
		if (!props.clickToOpen && !dismissedByPress && e.pointerType !== "touch") {
			showTooltipNow();
		}
	};

	const handlePointerLeave = () => {
		pointerInside = false;
		dismissedByPress = triggerHasOpenPopup();
		if (!props.clickToOpen) hideTooltip();
	};

	/**
	 * Solid delegates focus events along the component tree, so focus moving around a popover
	 * portaled from inside the trigger (its close button, say) arrives here too. Only focus in
	 * the trigger's own DOM counts.
	 */
	const isFocusInTrigger = (e: FocusEvent) =>
		(e.currentTarget as Node).contains(e.target as Node);

	const handleFocus = (e: FocusEvent) => {
		if (!isFocusInTrigger(e)) return;
		if (!props.clickToOpen && !lastPressWasTouch) scheduleOpen();
	};

	const handleBlur = (e: FocusEvent) => {
		if (!isFocusInTrigger(e)) return;
		dismissedByPress = triggerHasOpenPopup();
		if (!props.clickToOpen) hideTooltip();
	};

	/**
	 * Pointer-down covers mouse and touch presses; click covers keyboard activation. Presses
	 * inside the (portaled) tooltip itself also bubble here, so only the trigger counts.
	 */
	const dismissOnPress = (e: Event) => {
		if (props.clickToOpen || props.keepOpenOnClick) return;
		if (!triggerRef?.contains(e.target as Node)) return;
		dismissedByPress = true;
		hideTooltipNow();
	};

	const handleTriggerClick = (e: MouseEvent) => {
		if (!props.clickToOpen) {
			dismissOnPress(e);
			return;
		}
		e.stopPropagation();
		setIsVisible((prev) => !prev);
	};

	const handleClickOutside = (e: MouseEvent) => {
		if (!isVisible()) return;
		const target = e.target as Node;
		// Close if click is outside both trigger and tooltip
		if (
			triggerRef &&
			!triggerRef.contains(target) &&
			tooltipRef &&
			!tooltipRef.contains(target)
		) {
			setIsVisible(false);
		}
	};

	createEffect(() => {
		if (!props.clickToOpen) {
			return;
		}
		document.addEventListener("mousedown", handleClickOutside);
		onCleanup(() => {
			document.removeEventListener("mousedown", handleClickOutside);
		});
	});

	createEffect(() => {
		if (!isVisible()) {
			return;
		}
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key !== "Escape") {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			if (props.clickToOpen) {
				setIsVisible(false);
				return;
			}
			// Match press-to-dismiss: stay closed while focus remains on the trigger.
			dismissedByPress = true;
			hideTooltipNow();
		};
		document.addEventListener("keydown", handleEscape, true);
		onCleanup(() => {
			document.removeEventListener("keydown", handleEscape, true);
		});
	});

	const describedByOriginals = new Map<HTMLElement, string | null>();
	const expandedOriginals = new Map<HTMLElement, string | null>();

	createEffect(() => {
		const visible = isVisible();
		const root = triggerRef;
		if (!root) {
			return;
		}
		const targets = getDescribableElements(root);
		if (visible) {
			for (const el of targets) {
				mergeDescribedBy(el, tooltipId, describedByOriginals);
			}
		} else {
			restoreDescribedBy(describedByOriginals);
		}

		if (props.clickToOpen) {
			for (const el of targets) {
				if (!expandedOriginals.has(el)) {
					expandedOriginals.set(el, el.getAttribute("aria-expanded"));
				}
				el.setAttribute("aria-expanded", visible ? "true" : "false");
			}
		}

		onCleanup(() => {
			restoreDescribedBy(describedByOriginals);
			if (props.clickToOpen) {
				for (const [el, value] of expandedOriginals) {
					if (value === null) {
						el.removeAttribute("aria-expanded");
					} else {
						el.setAttribute("aria-expanded", value);
					}
				}
				expandedOriginals.clear();
			}
		});
	});

	const updateTooltipPosition = () => {
		if (!triggerRef || !tooltipRef) return;

		const triggerRect = anchorRectFor(triggerRef, props.anchorTo, placement());
		
		// Get the actual tooltip element to measure
		const tooltipElement = tooltipRef.querySelector('.gvtui-tooltip') as HTMLElement;
		if (!tooltipElement) {
			// Tooltip element not found yet, retry
			setTimeout(() => updateTooltipPosition(), 0);
			return;
		}
		
		const tooltipRect = tooltipElement.getBoundingClientRect();
		
		// If tooltip hasn't been measured yet (dimensions are 0), retry
		if (tooltipRect.width === 0 || tooltipRect.height === 0) {
			setTimeout(() => updateTooltipPosition(), 0);
			return;
		}

		// Always use the current placement prop value (not from element attribute)
		const placementValue = placement();
		
		// Ensure tooltip element is visible
		tooltipElement.style.display = 'block';
		tooltipElement.style.visibility = 'visible';
		const gap = 8; // Gap between trigger and tooltip

		// Use viewport coordinates (getBoundingClientRect gives viewport coordinates)
		// Since tooltip is portaled to body, we use fixed positioning
		let top = 0;
		let left = 0;

		// Parse placement to determine primary direction and alignment
		const parts = placementValue.split(" ");
		const primary = parts[0]; // top, bottom, left, right, start, end
		const alignment = parts[1] || "center"; // left, right, top, bottom, start, end, or center
		// Map start/end to left/right based on text direction (assuming LTR for now)
		const isRTL = false; // Could be determined from document direction
		let resolvedPrimary =
			primary === "start"
				? isRTL
					? "right"
					: "left"
				: primary === "end"
					? isRTL
						? "left"
						: "right"
					: primary;
		const resolvedAlignment =
			alignment === "start"
				? isRTL
					? "right"
					: "left"
				: alignment === "end"
					? isRTL
						? "left"
						: "right"
					: alignment;

		// Get viewport dimensions
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		// Calculate initial position based on primary direction using viewport coordinates
		let calculatedTop = 0;
		let calculatedLeft = 0;

		switch (resolvedPrimary) {
			case "top":
				calculatedTop = triggerRect.top - tooltipRect.height - gap;
				// Horizontal alignment
				if (resolvedAlignment === "left") {
					calculatedLeft = triggerRect.left;
				} else if (resolvedAlignment === "right") {
					calculatedLeft = triggerRect.left + triggerRect.width - tooltipRect.width;
				} else {
					// center (default)
					calculatedLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
				}
				break;

			case "bottom":
				calculatedTop = triggerRect.bottom + gap;
				// Horizontal alignment
				if (resolvedAlignment === "left") {
					calculatedLeft = triggerRect.left;
				} else if (resolvedAlignment === "right") {
					calculatedLeft = triggerRect.left + triggerRect.width - tooltipRect.width;
				} else {
					// center (default)
					calculatedLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
				}
				break;

			case "left":
				calculatedLeft = triggerRect.left - tooltipRect.width - gap;
				// Vertical alignment
				if (resolvedAlignment === "top") {
					calculatedTop = triggerRect.top;
				} else if (resolvedAlignment === "bottom") {
					calculatedTop = triggerRect.bottom - tooltipRect.height;
				} else {
					// center (default)
					calculatedTop = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
				}
				break;

			case "right":
				calculatedLeft = triggerRect.right + gap;
				// Vertical alignment
				if (resolvedAlignment === "top") {
					calculatedTop = triggerRect.top;
				} else if (resolvedAlignment === "bottom") {
					calculatedTop = triggerRect.bottom - tooltipRect.height;
				} else {
					// center (default)
					calculatedTop = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
				}
				break;
		}

		const allowFlip = props.flip !== false;

		// Check viewport collisions and flip placement if needed
		const tooltipRight = calculatedLeft + tooltipRect.width;
		const tooltipBottom = calculatedTop + tooltipRect.height;

		// Check vertical collisions (top/bottom)
		if (allowFlip && resolvedPrimary === "top" && calculatedTop < 0) {
			// Tooltip would be above viewport, flip to bottom
			resolvedPrimary = "bottom";
			calculatedTop = triggerRect.bottom + gap;
			// Recalculate horizontal alignment
			if (resolvedAlignment === "left") {
				calculatedLeft = triggerRect.left;
			} else if (resolvedAlignment === "right") {
				calculatedLeft = triggerRect.left + triggerRect.width - tooltipRect.width;
			} else {
				calculatedLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
			}
		} else if (
			allowFlip &&
			resolvedPrimary === "bottom" &&
			tooltipBottom > viewportHeight
		) {
			// Tooltip would be below viewport, flip to top
			resolvedPrimary = "top";
			calculatedTop = triggerRect.top - tooltipRect.height - gap;
			// Recalculate horizontal alignment
			if (resolvedAlignment === "left") {
				calculatedLeft = triggerRect.left;
			} else if (resolvedAlignment === "right") {
				calculatedLeft = triggerRect.left + triggerRect.width - tooltipRect.width;
			} else {
				calculatedLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
			}
		}

		// Check horizontal collisions (left/right)
		if (allowFlip && resolvedPrimary === "left" && calculatedLeft < 0) {
			// Tooltip would be to the left of viewport, flip to right
			resolvedPrimary = "right";
			calculatedLeft = triggerRect.right + gap;
			// Recalculate vertical alignment
			if (resolvedAlignment === "top") {
				calculatedTop = triggerRect.top;
			} else if (resolvedAlignment === "bottom") {
				calculatedTop = triggerRect.bottom - tooltipRect.height;
			} else {
				calculatedTop = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
			}
		} else if (
			allowFlip &&
			resolvedPrimary === "right" &&
			tooltipRight > viewportWidth
		) {
			// Tooltip would be to the right of viewport, flip to left
			resolvedPrimary = "left";
			calculatedLeft = triggerRect.left - tooltipRect.width - gap;
			// Recalculate vertical alignment
			if (resolvedAlignment === "top") {
				calculatedTop = triggerRect.top;
			} else if (resolvedAlignment === "bottom") {
				calculatedTop = triggerRect.bottom - tooltipRect.height;
			} else {
				calculatedTop = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
			}
		}

		// Shift horizontally to stay in the viewport; only clamp vertical position when flipping is allowed.
		left = Math.max(0, Math.min(calculatedLeft, viewportWidth - tooltipRect.width));
		top = allowFlip
			? Math.max(0, Math.min(calculatedTop, viewportHeight - tooltipRect.height))
			: calculatedTop;

		// Update data-placement attribute to reflect the actual placement used
		const actualPlacement = resolvedAlignment === "center" 
			? resolvedPrimary 
			: `${resolvedPrimary} ${resolvedAlignment}`;
		tooltipElement.setAttribute('data-placement', actualPlacement);
		setTooltipArrowOffset(
			tooltipElement,
			triggerRect,
			left,
			tooltipRect.width,
			resolvedPrimary,
			resolvedAlignment,
		);

		// Set positioning styles - use fixed positioning for portaled tooltip
		tooltipRef.style.position = "fixed";
		tooltipRef.style.left = `${left}px`;
		tooltipRef.style.top = `${top}px`;
		tooltipRef.style.zIndex = "10000";
		// Hover mode needs pointer events so the tip stays open while the cursor is over it
		tooltipRef.style.pointerEvents = "auto";
		tooltipRef.style.margin = "0";
		tooltipRef.style.transform = "none";
		// Safe to reveal now that it has somewhere to be
		tooltipRef.style.visibility = "visible";
	};

	// Child `<Tooltip>` nodes are resolved before the portaled provider runs; ensure the
	// tooltip root id matches what we put on `aria-describedby`.
	createEffect(() => {
		if (!isVisible()) {
			return;
		}
		queueMicrotask(() => {
			const tip = tooltipRef?.querySelector(".gvtui-tooltip") as
				| HTMLElement
				| null
				| undefined;
			if (tip && tip.id !== tooltipId) {
				tip.id = tooltipId;
			}
		});
	});

	// Update position when tooltip becomes visible or placement changes
	createEffect(() => {
		const visible = isVisible();
		if (visible && triggerRef && tooltipRef) {
			// Position in the same tick the tooltip mounts, so it is placed before the first
			// paint rather than jumping a couple of frames later.
			updateTooltipPosition();
			// Use double requestAnimationFrame to ensure DOM is fully updated and measured
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					updateTooltipPosition();
				});
			});

			// Re-position on scroll or resize so tooltip follows its anchor
			const reposition = () => updateTooltipPosition();
			window.addEventListener("scroll", reposition, true);
			window.addEventListener("resize", reposition);

			const sizeObserver =
				typeof ResizeObserver !== "undefined"
					? new ResizeObserver(reposition)
					: undefined;
			sizeObserver?.observe(tooltipRef);

			onCleanup(() => {
				window.removeEventListener("scroll", reposition, true);
				window.removeEventListener("resize", reposition);
				sizeObserver?.disconnect();
			});
		}
	});

	// A trigger can be enabled or disabled while its tooltip is showing (e.g. once the data
	// it acts on loads), or open a popup without being clicked (arrow keys on a menu button),
	// so this is watched for as long as it is open, not read once.
	createEffect(() => {
		if (!isVisible() || !triggerRef) {
			return;
		}
		const trigger = triggerRef;
		const update = () => {
			setTriggerDisabled(
				trigger.querySelector(":disabled, [aria-disabled='true']") !== null,
			);
			if (triggerHasOpenPopup()) {
				dismissedByPress = true;
				hideTooltipNow();
			}
		};
		update();
		const observer = new MutationObserver(update);
		observer.observe(trigger, {
			subtree: true,
			childList: true,
			attributes: true,
			attributeFilter: ["disabled", "aria-disabled", "aria-expanded"],
		});
		onCleanup(() => observer.disconnect());
	});

	onCleanup(() => {
		clearHideTimeout();
		clearShowDelay();
	});

	const children = getTooltipChildren(props.children);
	const trigger = children[0];
	const tooltip = props.renderContent ? undefined : children[1];
	return (
		<TooltipShellContext.Provider value={{ tooltipId }}>
			<span
				class={`gvtui-tooltip-trigger${props.class ? ` ${props.class}` : ""}`}
				tabIndex={props.tabIndex}
				onPointerEnter={handleTriggerPointerEnter}
				onPointerLeave={handlePointerLeave}
				onFocusIn={handleFocus}
				onFocusOut={handleBlur}
				onPointerDown={dismissOnPress}
				onClick={handleTriggerClick}
				style={props.clickToOpen ? "cursor: pointer;" : undefined}
			>
				<span ref={triggerRef} class="gvtui-tooltip-trigger-wrapper">
					{trigger}
				</span>
				<TooltipTriggerWrapper
					tooltipId={tooltipId}
					isVisible={isVisible}
					openedWarm={openedWarm}
					triggerDisabled={triggerDisabled}
					tooltipRef={(el) => {
						tooltipRef = el;
					}}
					tooltip={tooltip}
					renderContent={props.renderContent}
					placement={placement}
					onPointerEnter={handleTooltipBridgePointerEnter}
					onPointerLeave={handlePointerLeave}
				/>
			</span>
		</TooltipShellContext.Provider>
	);
};

export const TooltipTrigger = (props: TooltipTriggerProps) => {
	const [local, rest] = splitProps(props, ["disabled"]);
	// Boolean `when` — do not pass `() => ...` to Show (a function is truthy and always shows children).
	const showTooltipChrome = createMemo(() => local.disabled !== true);
	return (
		<Show
			when={showTooltipChrome()}
			fallback={<TooltipTriggerPassthrough children={props.children} />}
		>
			<TooltipTriggerInner {...rest} />
		</Show>
	);
};

const TooltipTriggerWrapper = (props: {
	tooltipId: string;
	tooltip?: JSX.Element;
	renderContent?: () => JSX.Element;
	isVisible: Accessor<boolean>;
	openedWarm: Accessor<boolean>;
	triggerDisabled: Accessor<boolean>;
	tooltipRef: (el: HTMLSpanElement) => void;
	placement: Accessor<TooltipPlacement>;
	onPointerEnter: (e: PointerEvent) => void;
	onPointerLeave: () => void;
}) => {
	return (
		<Show when={props.isVisible()}>
			<Portal mount={document.body}>
				<TooltipShellContext.Provider value={{ tooltipId: props.tooltipId }}>
					<span
						ref={props.tooltipRef}
						class={`gvtui-tooltip-wrapper${props.openedWarm() ? " gvtui-tooltip-wrapper--instant" : ""}`}
						// Stays hidden until positioned, so it never paints at its unpositioned
						// static location first.
						style="pointer-events: auto; visibility: hidden;"
						data-placement={props.placement()}
						data-trigger-disabled={props.triggerDisabled() ? "" : undefined}
						onPointerEnter={props.onPointerEnter}
						onPointerLeave={props.onPointerLeave}
					>
						{props.renderContent ? props.renderContent() : props.tooltip}
					</span>
				</TooltipShellContext.Provider>
			</Portal>
		</Show>
	);
};
